import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Typography,
  Box,
  InputAdornment,
  Alert,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  IconButton,
  Paper,
  Chip
} from '@mui/material';
import {
  LocationOn,
  MyLocation,
  Search,
  CheckCircle,
  Close,
  Place,
  Map,
  GpsFixed
} from '@mui/icons-material';
import { GoogleMap, Marker, InfoWindow } from '@react-google-maps/api';
import { GOOGLE_MAPS_CONFIG } from '../config/maps';
import { useGoogleMaps } from '../hooks/useGoogleMaps';

const EnhancedLocationDialog = ({ open, onClose, onLocationSelect, currentAddress }) => {
  const [location, setLocation] = useState(null);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [mapCenter, setMapCenter] = useState(GOOGLE_MAPS_CONFIG.DEFAULT_CENTER);
  const [autocompleteService, setAutocompleteService] = useState(null);
  const [geocoderService, setGeocoderService] = useState(null);
  const [mapRef, setMapRef] = useState(null);
  const [mapsLoaded, setMapsLoaded] = useState(false);
  const searchInputRef = useRef(null);

  // Use global Google Maps loading
  const { isLoaded: googleMapsLoaded, isLoading: googleMapsLoading, error: googleMapsError } = useGoogleMaps();

  const mapContainerStyle = {
    width: '100%',
    height: '400px'
  };

  const defaultCenter = {
    lat: -37.8136,
    lng: 144.9631
  };

  // Initialize Google Maps services when global loading completes
  useEffect(() => {
    if (googleMapsLoaded && window.google && window.google.maps) {
      try {
        // Check if Places API is available
        if (window.google.maps.places && window.google.maps.places.AutocompleteService) {
          setAutocompleteService(new window.google.maps.places.AutocompleteService());
        }
        
        // Check if Geocoder is available
        if (window.google.maps.Geocoder) {
          setGeocoderService(new window.google.maps.Geocoder());
        }
        
        setMapsLoaded(true);
        setError(''); // Clear any previous errors
      } catch (error) {
        setError('Failed to initialize Google Maps services: ' + error.message);
      }
    } else if (googleMapsError) {
      setError('Failed to load Google Maps: ' + googleMapsError.message);
    }
  }, [googleMapsLoaded, googleMapsError]);

  // Ensure geocoder service is available when needed
  useEffect(() => {
    if (googleMapsLoaded && window.google && window.google.maps && !geocoderService) {
      try {
        if (window.google.maps.Geocoder) {
          setGeocoderService(new window.google.maps.Geocoder());
        }
      } catch (error) {
        // Silently handle error
      }
    }
  }, [googleMapsLoaded, geocoderService]);

  // Initialize map reference
  const onMapLoad = useCallback((map) => {
    setMapRef(map);
    setMapsLoaded(true);
  }, []);

  // Get current location
  const getCurrentLocation = async () => {
    try {
      setLoading(true);
      setError('');

      if (!navigator.geolocation) {
        setError('Geolocation is not supported by this browser');
        return;
      }

      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const { latitude, longitude } = position.coords;
          const newCenter = { lat: latitude, lng: longitude };
          
          setMapCenter(newCenter);
          setLocation(newCenter);
          
          // Get address from coordinates using backend API
          try {
            // Get auth token
            const customerToken = localStorage.getItem('customerToken');
            const token = localStorage.getItem('token');
            const authToken = customerToken || token;
            
            const headers = {
              'Content-Type': 'application/json',
            };
            if (authToken) {
              headers['Authorization'] = `Bearer ${authToken}`;
            }
            
            const response = await fetch(`${process.env.REACT_APP_API_URL || 'https://api.nistylez.com/api'}/locations/reverse-geocode`, {
              method: 'POST',
              headers: headers,
              body: JSON.stringify({
                lat: latitude,
                lng: longitude
              })
            });
            
            const data = await response.json();
            console.log('🔍 Customer App - Reverse geocode response:', data);
            
            // Try different response structures
            let address = null;
            
            if (data.success && data.data && data.data.address) {
              address = data.data.address;
            } else if (data.data && data.data.address) {
              address = data.data.address;
            } else if (data.address) {
              address = data.address;
            } else if (data.success && data.data && data.data.formatted_address) {
              address = data.data.formatted_address;
            } else if (data.data && data.data.formatted_address) {
              address = data.data.formatted_address;
            } else if (data.formatted_address) {
              address = data.formatted_address;
            }
            
            if (address) {
              console.log('🔍 Customer App - Got full address:', address);
              setSelectedLocation({
                address,
                lat: latitude,
                lng: longitude,
                type: 'current'
              });
              setSearchQuery(address);
            } else {
              console.log('🔍 Customer App - No valid address found in response');
              setError('Could not get address from location. Please try searching for your address instead.');
            }
          } catch (error) {
            console.error('Reverse geocoding failed:', error);
            setError('Failed to get address from location. Please try searching for your address instead.');
          }
          
          setLoading(false);
        },
        (error) => {
          setError('Unable to get your current location');
          setLoading(false);
        }
      );
    } catch (error) {
      setError('Error getting location');
      setLoading(false);
    }
  };

  // Handle search input changes
  const handleSearchChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query);

    if (query.length > 2) {
      if (autocompleteService && mapsLoaded) {
        autocompleteService.getPlacePredictions(
          {
            input: query,
            types: ['geocode', 'establishment']
          },
          (predictions, status) => {
            if (status === 'OK' && predictions) {
              setSuggestions(predictions);
            } else {
              setSuggestions([]);
            }
          }
        );
      } else {
        setSuggestions([]);
      }
    } else {
      setSuggestions([]);
    }
  };

  // Backend geocoding fallback
  const searchWithBackend = async (query) => {
    try {
      // Get auth token
      const customerToken = localStorage.getItem('customerToken');
      const token = localStorage.getItem('token');
      const authToken = customerToken || token;
      
      const headers = { 'Content-Type': 'application/json' };
      if (authToken) {
        headers['Authorization'] = `Bearer ${authToken}`;
      }
      
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'https://api.nistylez.com/api'}/locations/geocode`, {
        method: 'POST',
        headers: headers,
        body: JSON.stringify({ address: query })
      });
      const data = await response.json();
      
      if (data.success && data.data && data.data.length > 0) {
        // Convert backend results to Google Maps format
        const backendSuggestions = data.data.map((result, index) => ({
          place_id: result.place_id || `backend_${Date.now()}_${index}`,
          description: result.formatted_address,
          structured_formatting: {
            main_text: result.formatted_address.split(',')[0],
            secondary_text: result.formatted_address.split(',').slice(1).join(',').trim()
          },
          geometry: {
            location: {
              lat: result.geometry.location.lat,
              lng: result.geometry.location.lng
            }
          }
        }));
        setSuggestions(backendSuggestions);
      } else {
        setSuggestions([]);
      }
    } catch (error) {
      setSuggestions([]);
    }
  };

  // Handle location selection from search
  const handleSelectSuggestion = (suggestion) => {
    if (geocoderService) {
      geocoderService.geocode(
        { placeId: suggestion.place_id },
        (results, status) => {
          if (status === 'OK' && results[0]) {
            const result = results[0];
            const location = result.geometry.location;
            const address = result.formatted_address;
            
            const newLocation = {
              lat: location.lat(),
              lng: location.lng()
            };

            setMapCenter(newLocation);
            setSelectedLocation({
              address,
              lat: newLocation.lat,
              lng: newLocation.lng,
              type: 'searched'
            });
            setSearchQuery(address);
            setSuggestions([]);
          }
        }
      );
    }
  };


  // Confirm location selection
  const handleConfirmLocation = () => {
    if (selectedLocation && onLocationSelect) {
      onLocationSelect(selectedLocation);
      
      // Dispatch custom event for real-time updates
      window.dispatchEvent(new CustomEvent('locationUpdated', {
        detail: {
          address: selectedLocation.address,
          fullAddress: selectedLocation.address,
          lat: selectedLocation.lat,
          lng: selectedLocation.lng,
          type: selectedLocation.type
        }
      }));
      
      onClose();
    }
  };

  // Load current address when dialog opens
  useEffect(() => {
    if (open && currentAddress) {
      setSearchQuery(currentAddress);
      // Clear previous selected location to avoid showing old pin
      setSelectedLocation(null);
      // Geocode immediately
      geocodeAndShowPin(currentAddress);
      setSuggestions([]);
      setError('');
    }
  }, [open, currentAddress]);

  // Helper function to geocode address and show pin
  const geocodeAndShowPin = async (address) => {
    if (!address) return;
    
    try {
      // Get auth token
      const customerToken = localStorage.getItem('customerToken');
      const token = localStorage.getItem('token');
      const authToken = customerToken || token;
      
      const headers = { 'Content-Type': 'application/json' };
      if (authToken) {
        headers['Authorization'] = `Bearer ${authToken}`;
      }
      
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'https://api.nistylez.com/api'}/locations/geocode`, {
        method: 'POST',
        headers: headers,
        body: JSON.stringify({ address })
      });
      
      const data = await response.json();
      
      // Handle the API response format
      if (data.success && data.data && data.data.length > 0) {
        const result = data.data[0];
        const location = {
          lat: result.geometry.location.lat,
          lng: result.geometry.location.lng
        };
        
        setMapCenter(location);
        
        const locationData = {
          address: result.formatted_address,
          lat: location.lat,
          lng: location.lng,
          type: 'current'
        };
        
        setSelectedLocation(locationData);
      }
    } catch (error) {
      // Handle error silently
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: { height: '90vh' }
      }}
    >
      <DialogTitle sx={{ backgroundColor: '#1a237e', color: 'white' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6">Set Your Location</Typography>
          <IconButton onClick={onClose} sx={{ color: 'white' }}>
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column' }}>
        {/* Search Section */}
        <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
          <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
            Search for a location or click on the map to pin your location
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
            <TextField
              ref={searchInputRef}
              fullWidth
              placeholder="Search for a location..."
              value={searchQuery}
              onChange={handleSearchChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search color="action" />
                  </InputAdornment>
                ),
                endAdornment: searchQuery && (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => {
                        setSearchQuery('');
                        setSuggestions([]);
                        setError('');
                      }}
                      edge="end"
                      size="small"
                      title="Clear search"
                    >
                      <Close fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <Button
              variant="outlined"
              onClick={getCurrentLocation}
              disabled={loading}
              startIcon={loading ? <CircularProgress size={20} /> : <MyLocation />}
              sx={{ 
                borderColor: '#1a237e',
                color: '#1a237e',
                '&:hover': {
                  borderColor: '#0d47a1',
                  backgroundColor: 'rgba(26, 35, 126, 0.04)'
                }
              }}
            >
              {loading ? 'Getting...' : 'Current'}
            </Button>
          </Box>

          {/* Search Suggestions */}
          {suggestions.length > 0 && (
            <Paper sx={{ maxHeight: 200, overflow: 'auto' }}>
              <List>
                {suggestions.map((suggestion, index) => (
                  <React.Fragment key={index}>
                    <ListItem 
                      button 
                      onClick={() => handleSelectSuggestion(suggestion)}
                    >
                      <ListItemIcon>
                        <Place color="action" />
                      </ListItemIcon>
                      <ListItemText 
                        primary={suggestion.structured_formatting.main_text}
                        secondary={suggestion.structured_formatting.secondary_text}
                      />
                    </ListItem>
                    {index < suggestions.length - 1 && <Divider />}
                  </React.Fragment>
                ))}
              </List>
            </Paper>
          )}

          {/* Selected Location Display */}
          {selectedLocation && (
            <Alert severity="success" sx={{ mt: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CheckCircle color="success" />
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                    Selected Location:
                  </Typography>
                  <Typography variant="body2">
                    {selectedLocation.address}
                  </Typography>
                  <Chip 
                    label={selectedLocation.type === 'current' ? 'Current Location' : 
                           selectedLocation.type === 'searched' ? 'Searched' : 'Pinned on Map'}
                    size="small"
                    color={selectedLocation.type === 'current' ? 'primary' : 'default'}
                    sx={{ mt: 0.5 }}
                  />
                </Box>
              </Box>
            </Alert>
          )}
        </Box>

        {/* Map Section */}
        <Box sx={{ flex: 1, position: 'relative' }}>
          {googleMapsError ? (
            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'center', 
              justifyContent: 'center', 
              height: '400px',
              backgroundColor: '#f5f5f5',
              gap: 2
            }}>
              <Typography variant="body2" color="error" sx={{ mb: 2 }}>
                Failed to load Google Maps: {googleMapsError.message}
              </Typography>
              <Button
                variant="outlined"
                onClick={getCurrentLocation}
                disabled={loading}
                startIcon={loading ? <CircularProgress size={20} /> : <MyLocation />}
              >
                {loading ? 'Getting Location...' : 'Use Current Location'}
              </Button>
            </Box>
          ) : (
                         <GoogleMap
               mapContainerStyle={mapContainerStyle}
               center={selectedLocation ? { lat: selectedLocation.lat, lng: selectedLocation.lng } : mapCenter}
               zoom={GOOGLE_MAPS_CONFIG.DEFAULT_ZOOM}
                onLoad={onMapLoad}
               options={GOOGLE_MAPS_CONFIG.MAP_OPTIONS}
             >
              {/* Show marker when we have a selected location */}
              {selectedLocation && (
                <Marker
                  position={{ lat: selectedLocation.lat, lng: selectedLocation.lng }}
                  icon={window.google && window.google.maps && window.google.maps.Size ? {
                    url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(`
                      <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M16 2C10.5 2 6 6.5 6 12c0 7 10 20 10 20s10-13 10-20c0-5.5-4.5-10-10-10zm0 13c-1.7 0-3-1.3-3-3s1.3-3 3-3 3 1.3 3 3-1.3 3-3 3z" fill="#1976d2" stroke="white" stroke-width="2"/>
                      </svg>
                    `),
                    scaledSize: new window.google.maps.Size(32, 32),
                    anchor: new window.google.maps.Point(16, 16)
                  } : undefined}
                />
              )}
             </GoogleMap>
          )}

        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
        <Button onClick={onClose}>
          Cancel
        </Button>
        <Button 
          onClick={handleConfirmLocation}
          variant="contained"
          disabled={!selectedLocation}
          sx={{ backgroundColor: '#1a237e' }}
        >
          Confirm Location
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EnhancedLocationDialog;
